import { formatDistanceToNow } from 'date-fns'
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import type { OwnedPrompt } from '../../api/owned-prompts'
import PromptMarkdown from './PromptMarkdown'

interface PromptMasterDetailProps {
  prompts: OwnedPrompt[]
  scopeLabel: (prompt: OwnedPrompt) => string
  selectedId: string | null
  onSelect: (id: string | null) => void
  renderActions: (prompt: OwnedPrompt) => ReactNode
  emptyState: ReactNode
  /** Tailwind height for the two-pane area; panes scroll independently inside it. */
  heightClass?: string
}

/**
 * Master/detail prompt browser: a compact searchable list on the left and the selected
 * prompt's full details on the right. Below `md` only one pane shows at a time.
 */
export default function PromptMasterDetail({
  prompts,
  scopeLabel,
  selectedId,
  onSelect,
  renderActions,
  emptyState,
  heightClass = 'h-[calc(100vh-15rem)] min-h-[420px]',
}: PromptMasterDetailProps) {
  const [search, setSearch] = useState('')
  const [mobileDetail, setMobileDetail] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q === ''
      ? prompts
      : prompts.filter(
          (p) =>
            p.title.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q)
        )
  }, [prompts, search])

  // A stale or forbidden selection falls back to the first visible prompt.
  const selected = filtered.find((p) => p.id === selectedId) ?? filtered[0] ?? null

  useEffect(() => {
    if (mobileDetail) headingRef.current?.focus()
  }, [mobileDetail])

  function select(id: string, opts?: { openDetail?: boolean }) {
    onSelect(id)
    if (opts?.openDetail) setMobileDetail(true)
  }

  function onListKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const index = filtered.findIndex((p) => p.id === selected?.id)
    const next = filtered[e.key === 'ArrowDown' ? index + 1 : index - 1]
    if (!next) return
    select(next.id)
    listRef.current?.querySelector<HTMLElement>(`[data-prompt-id="${next.id}"]`)?.focus()
  }

  if (prompts.length === 0) return <div>{emptyState}</div>

  return (
    <div className={`${heightClass} flex overflow-hidden rounded-lg border border-harbor-border`}>
      <aside
        className={`${mobileDetail ? 'hidden' : 'flex'} w-full shrink-0 flex-col border-harbor-border md:flex md:w-80 md:border-r`}
      >
        <div className="border-b border-harbor-border p-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search prompts…"
            aria-label="Search prompts"
            className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-1.5 text-sm text-harbor-text placeholder:text-harbor-text-muted"
          />
        </div>
        {filtered.length === 0 ? (
          <p className="p-4 text-sm text-harbor-text-secondary">No prompts match your search.</p>
        ) : (
          <ul
            ref={listRef}
            aria-label="Prompts"
            onKeyDown={onListKeyDown}
            className="flex-1 divide-y divide-harbor-border overflow-y-auto"
          >
            {filtered.map((prompt) => {
              const active = prompt.id === selected?.id
              return (
                <li key={prompt.id}>
                  <button
                    type="button"
                    data-prompt-id={prompt.id}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => select(prompt.id, { openDetail: true })}
                    className={`block w-full px-4 py-3 text-left transition-colors ${
                      active ? 'bg-harbor-surface-raised' : 'hover:bg-harbor-surface-raised/60'
                    }`}
                  >
                    <span className="block truncate text-sm font-medium text-harbor-text">
                      {prompt.title}
                    </span>
                    <span className="mt-1 flex items-center gap-2 text-xs text-harbor-text-muted">
                      <span className="rounded border border-harbor-border px-1.5 py-0.5">
                        {scopeLabel(prompt)}
                      </span>
                      <span>
                        {formatDistanceToNow(new Date(prompt.updatedAt), { addSuffix: true })}
                      </span>
                    </span>
                    {prompt.description && (
                      <span className="mt-1 block truncate text-xs text-harbor-text-secondary">
                        {prompt.description}
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </aside>

      <section
        aria-label="Prompt details"
        className={`${mobileDetail ? 'block' : 'hidden'} min-w-0 flex-1 overflow-y-auto p-5 md:block`}
      >
        {selected === null ? (
          <p className="text-sm text-harbor-text-secondary">Select a prompt to see its details.</p>
        ) : (
          <article>
            <button
              type="button"
              onClick={() => setMobileDetail(false)}
              className="mb-3 text-sm text-harbor-text-secondary hover:text-harbor-text md:hidden"
            >
              ← Back to list
            </button>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2
                  ref={headingRef}
                  tabIndex={-1}
                  className="text-lg font-semibold text-harbor-text outline-none"
                >
                  {selected.title}
                </h2>
                <p className="mt-1 text-xs text-harbor-text-muted">
                  {scopeLabel(selected)} · Updated{' '}
                  {formatDistanceToNow(new Date(selected.updatedAt), { addSuffix: true })}
                  {selected.usageCount > 0 && ` · Used ${selected.usageCount}×`}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">{renderActions(selected)}</div>
            </div>
            {selected.description && (
              <p className="mt-3 text-sm text-harbor-text-secondary">{selected.description}</p>
            )}
            <div className="mt-4">
              <PromptMarkdown markdown={selected.body} maxHeightClass="max-h-none" />
            </div>
          </article>
        )}
      </section>
    </div>
  )
}
